import objectToFormData from "object-to-formdata";
import axios from "axios/index";
import _ from "lodash";
import AppConfig from "../config/AppConfig";
import userStore from "../stores/UserStore";
import I18n from "../common/I18n";
import { alertMessage, isEmpty } from "../common/Common";
import { AuthenticationService } from "./Auth";

import { clearSession, renderAlertMessage } from "../helpers/common";

// Add this before other interceptors
const CancelToken = axios.CancelToken;

export const isTokenValid = (token = {}) => {
  const now = Math.floor(Date.now() / 1000);
  const expiry = token.created_at + token.expires_in;
  if(now < expiry){
    return true;
  }
  return false;
};

export function isOktaUser() {
  try {
    const currentUser = localStorage.getItem('CURRENT_USER');
    if (!currentUser) return false;
    const user = JSON.parse(currentUser);
    return user && user.auth_source === 'okta';
  } catch {
    return false;
  }
}

export async function getRefreshToken (token_details) {
  if (!token_details || !token_details.refresh_token) {
    throw new Error('Invalid token details');
  }
  try {
    const response = await fetch(GenerateURL('oauth/token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        client_id: AppConfig.clientId,
        client_secret: AppConfig.clientSecret,
        grant_type: 'refresh_token',
        refresh_token: token_details.refresh_token,
      }),
    });
    if (!response.ok) {
      throw new Error('Token refresh failed');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(`Token refresh error: ${error.message}`);
  }
}

axios.interceptors.request.use(async config => {
  const token_details = JSON.parse(localStorage.getItem('auth') || '{}');
  // if not token details, return config
  if (!Object.keys(token_details).length) { return config; } // if user is not logged in, return config

  if (isTokenValid(token_details)) {
    const token = token_details.access_token;
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  } else {
    // Okta users: do not try refresh; go to login to re-authenticate via SSO
    if (isOktaUser()) {
      renderAlertMessage(I18n.t("auth_messages.session_expired_login_again"), 10);
      clearSession();
      return Promise.reject({
        response: {
          data: {
            errors: [ 'Session expired' ],
          },
        },
      });
    }
    try {
      const data = await getRefreshToken(token_details);
      const token = data.access_token;
      config.headers.Authorization = `Bearer ${token}`;
      localStorage.setItem('auth', JSON.stringify(data));
      return config;
    } catch (error) {
      alertMessage(I18n.t("auth_messages.session_expired_login_again"), "error");
      //localStorage.clear();
      clearSession();
      return Promise.reject({
        response: {
          data: {
            errors: [ error.message ],
          },
        },
      });
    }
  }
}, error => {
  return Promise.reject(error);
});

axios.interceptors.request.use(config => {
  if (config.signal) {
    // Create a cancel token source
    const source = CancelToken.source();
    
    // Add cancel token to config
    config.cancelToken = source.token;
    
    // Listen for abort signal
    config.signal.addEventListener('abort', () => {
      console.log('Aborting request for:', config.url);
      source.cancel('Request cancelled by AbortController');
    });
  }
  return config;
});

// similar one for response
axios.interceptors.response.use(response => {
  return response;
}, async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      // Okta users: go to login on 401, do not try refresh
      if (isOktaUser()) {
        alertMessage(I18n.t("auth_messages.session_expired_login_again"), "error");
        clearSession();
        return Promise.reject(error);
      }
      if(!originalRequest._retry) // if already tried, return error
      {
        clearSession();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const token_details = JSON.parse(localStorage.getItem('auth') || '{}');
      if (!Object.keys(token_details).length) { return Promise.reject(error); } // if user is not logged in, return config

      try {
        let data = {};
        if(isTokenValid(token_details)){
          data = token_details;
        }else{
          data = await getRefreshToken(token_details);
        }
        const token = data.access_token;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        localStorage.setItem('auth', JSON.stringify(data));
        return axios(originalRequest);
      } catch (error) {
        alertMessage(I18n.t("auth_messages.session_expired_login_again"), "error");
        //localStorage.clear();
        clearSession();
        return Promise.reject({
          response: {
            data: {
              errors: [ error.message ],
            },
          },

        })
      }
    }else{
      return Promise.reject(error);
    }
  }
)





export function PageParams(page, perPage) {
  return `page=${page || 1}&per_page=${perPage || AppConfig.perPage}`;
}

export function optionalPageParams(page, perPage, isReqPagination = true) {
  return `page=${page || 1}&per_page=${
    !isEmpty(perPage) ? perPage : isReqPagination ? AppConfig.perPage : ''
  }`;
}

export function ApiUrl(path) {
  return `${AppConfig.baseUrl}/api/${path}`;
}
export function javaUrl (path) {
  return `${AppConfig.javaUrl}${path}`;
}
export function javaUrlstage (path) {
  return `${AppConfig.javaUrlstage}${path}`;
}
export function javaWmsUrl (path) {
  return `${AppConfig.wmsUrl}${path}`;
}
export function javaShipper (path) {
  return `${AppConfig.javaShipper}${path}`;
}
export function linehaulJavaUrl (path) {
  return `${AppConfig.linehaulJavaUrl}${path}`;
}




export function javaWmsUrlPreCall (path) {
  return `${AppConfig.wmsUrl}${path}`;
}

export function GenerateURL(path) {
  return `${AppConfig.baseUrl}/${path}`;
}

export function HttpErrorMessage(error) {
  const response = {};
  if(!_.isString(error)){
    if (_.get(error, "response.data.errors", null)) {
      response.errors = error.response.data.errors;
    }
    const dataKeys = _.get(error, "response.data", {});
    _.forEach(dataKeys, (value, key) => {
      if (key !== "errors") {
        response[key] = !isEmpty(value) ? value : "";
      }
    });
    // Additive: the HTTP status code, so a caller can distinguish (e.g.) a
    // 404 "this record is already gone" from a 422 validation error without
    // parsing the errors array. See UnitRow.jsx's save() for the first
    // consumer (B-M6).
    const status = _.get(error, "response.status", null);
    if (status) {
      response.status = status;
    }
    // If `status` is the ONLY key, the body carried no renderable message, so
    // keep the human-readable fallback (renderAlertMessage would otherwise print
    // the bare object). status stays attached for callers that inspect it.
    if (Object.keys(response).length === 1 && response.status) {
      response.errors = [error?.message || I18n.t("messages.something_went_wrong")];
    }
    if (Object.keys(response).length > 0) {
      return response;
    }
    return [error?.message || I18n.t("messages.something_went_wrong")];
  } else {
    return [error]
  }
}

export function HttpErrorResponseKeys(error) {
  if (_.get(error, "response.data", {})) {
    const data = error.response.data || {};
    const response = {};
    Object.keys(data).forEach((key) => {
      response[key] = data[key];
    });
    return response;
  }
  return {};
}

export function ParseGeneralResponse(success, response, options) {
  let result = { success };

  if (!_.isEmpty(options.return)) {
    result.return = options.return;
  }
  if (success) {
    const dataKeys = options.responseDataKeys || {};
    _.forEach(dataKeys, (value, key) => {
      const valueKey = _.isEmpty(value) ? "data" : `data.${value}`;
      result[key] = _.get(response, valueKey, null);
    });
  } else {
    result = HttpErrorMessage(response);
  }
  return result;
}

export async function Call(method, url, data, parseCallback, options = {}, isPassOrgId = true, signal) {
  const currentUser = localStorage.getItem("CURRENT_USER") ? JSON.parse(localStorage.getItem("CURRENT_USER")) : null;
  if (currentUser) {
    const { roles, accounts } = currentUser;
    const account_ids = accounts && _.isArray(accounts) ? accounts.map(acc => acc.id).join(',') : ''
    const currentOrg = localStorage.getItem("selectedOrg")
    const isSuperAdmin = roles.includes("super_admin");
    const isShipper = roles.includes("shipper");
    const currentRoleParam = method === "get" ? `current_role=${roles[ 0 ]}` : '';
    const selectedWarehouses = localStorage.getItem("selectedWarehouses") || '';
    const warehouseIdsParam = selectedWarehouses.length < 1
        ? (JSON.parse(localStorage.getItem("warehouses")) ?? []).map(w => w.id).join(',')
        : selectedWarehouses;

    if (!isSuperAdmin && isPassOrgId) {
      if (method === "get") {
        url += `${url.includes("?") ? '&' : '?'}${currentRoleParam}`;
        url += !url.includes("org_id") ? `&org_id=${currentOrg}` : '';
        url += !url.includes("warehouse_ids") ? `&warehouse_ids=${warehouseIdsParam}` : '';
        url += isShipper && !url.includes("account_id") && !isEmpty(account_ids) ? `&account_id=${account_ids}` : '';
      } else if (!data.current_role) {
        data.current_role = roles[ 0 ];
      }
      if (data && !data.org_id) {
        data.org_id = currentOrg;
      }
    } else {
      if (method === "get") {
        url += `${url.indexOf("?") < 0 ? "?" : "&"}`;
        url += `current_role=${roles[0]}`;
        url += isShipper && !url.includes("account_id") && !isEmpty(account_ids) ? `&account_id=${account_ids}` : '';
      } else if (!data.current_role) {
        data.current_role = roles[0];
      }
    }
  }

  try {
    const config = {
      method,
      url,
      data,
      signal,
    };

    const response = await axios(config);
    
    if (isEmpty(response.data)) {
      postErrorToSlack({ url, method, payLoad: data, error: "Did not get the response" });
    }
    return parseCallback(true, response, options);
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Request cancelled:', error.message);
      const abortError = new Error('AbortError');
      abortError.name = 'AbortError';
      throw abortError;
    }
    
    if (_.isObject(error) && _.isObject(error.response)) {
      if (error.response.status === 401) {
        // Handle 401 error
      }
      if (![ 404, 422 ].includes(error.response.status) || !_.isObject(error.response.data)) {
        postErrorToSlack({ url, method, payLoad: data, error: error.response.data });
      }
    }
    return parseCallback(false, error, options);
  }
}

export function setObjectToFormData(data, options) {
  const currentUser = localStorage.getItem("CURRENT_USER") ? JSON.parse(localStorage.getItem("CURRENT_USER")) : null;
  const dataToPost = Object.assign({}, data);
  const current_role = userStore.getCurrentRole();
  if (!userStore.superAdmin() && !dataToPost.org_id) {
    dataToPost.org_id = userStore.getStateValue("selectedOrg");
    data.current_role = current_role;
  }
  // if(!userStore.superAdmin() && !dataToPost.selected_whs) {
  //   dataToPost.selected_whs = localStorage.getItem("selectedWarehouses");
  // }
  if( current_role === "shipper" && isEmpty(data.account_id) && !isEmpty(currentUser.accounts)){
    const account_ids = currentUser.accounts && _.isArray(currentUser.accounts) ? currentUser.accounts.map(acc => acc.id).join(',') : ''
    data.account_id = account_ids;
  }
  const formData = objectToFormData(dataToPost, options);
  console.log("options", options )
  return formData;
}

const replaceEmptyArraysWithNull = (obj) => {
  const newObj = Array.isArray(obj) ? [] : {};  // Create a new array/object

  for (const key in obj) {
    if (Array.isArray(obj[key]) && obj[key].length === 0) {
      // If the value is an empty array, replace it with null
      newObj[key] = null;
    } else if (typeof obj[key] === 'object' && obj[key] !== null && key !== 'picture_obj') {
      // Recursively handle nested objects or arrays
      newObj[key] = replaceEmptyArraysWithNull(obj[key]);
    } else {
      // Copy other types of values as they are
      newObj[key] = obj[key];
    }
  }

  return newObj;
};


export function seraializeFormData(data) {
  const currentUser = localStorage.getItem("CURRENT_USER") ? JSON.parse(localStorage.getItem("CURRENT_USER")) : null;
  const dataToPost = Object.assign({}, data);
  const current_role = userStore.getCurrentRole();
  if (!userStore.superAdmin() && !dataToPost.org_id) {
    dataToPost.org_id = userStore.getStateValue("selectedOrg");
    data.current_role = current_role;
  }
  // if(!userStore.superAdmin() && !dataToPost.selected_whs) {
  //   dataToPost.selected_whs = localStorage.getItem("selectedWarehouses");
  // }
  const options = { allowEmptyArrays: true };
  if( current_role === "shipper" && isEmpty(data.account_id) && !isEmpty(currentUser.accounts)){
    const account_ids = currentUser.accounts && _.isArray(currentUser.accounts) ? currentUser.accounts.map(acc => acc.id).join(',') : ''
    data.account_id = account_ids;
  }
  const cleanedData = replaceEmptyArraysWithNull(dataToPost)
  const formData = objectToFormData(cleanedData, options);
  return formData;
}

export function buildQueryString (obj, encodeReq=true){
  return Object.keys(obj)
    .map((key) => {
      return encodeReq
        ? `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
        : `${key}=${obj[key]}`;
    }).join("&");
}

export function postErrorToSlack ({url, payLoad, method, error = '', errorInfo = {}}){
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    let raw = "";
    if(url){
      raw = "URL: "+ url + "\n";
    }
    if(method && method.toLowerCase() !== 'get') {
      raw += "payLoad: "+ JSON.stringify(payLoad) + "\n";
    }
    if(!isEmpty(error)) {
      if(_.isArray(error)){
        raw += '\nError' +  error.join(', ');
      }else if(_.isObject(error)){ 
        raw += '\nError Stack' +  (error.stack ? error.stack : error.message ? error.message : '');
      }else {
        raw += '\nError' +  error.toString();
      }
    }





    if(errorInfo?.componentStack){
      raw += '\nError Stack' +  errorInfo.componentStack;
    }


    
    // exclude the error message if error string has
  const toExclude = [ 'googletag', 'appendChild', 'Invariant failed', 'Too Large', 'URL: /api/'];

    if(toExclude.some((ex) => raw.includes(ex))){
      return;
    }
    
    const reqUrl = ApiUrl("v1/users/ui_exception?operation_code=EPS");
    var requestOptions = {
      url: reqUrl,
      method: 'POST',
      headers: myHeaders,
      data: {message: raw},
      
      redirect: 'follow'
    };
     axios({...requestOptions})
      .then((response) => {
      })
      .catch((error) => {
      });
    // fetch(reqUrl, requestOptions)
    //   .then(response => response.text())
}

